import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent } from "@/components/ui/card";
import { GripVertical, Check } from "lucide-react";
import { answerLabel, normalizeAnswerCapitalization } from "@/utils/sortAnswers";
import { levelLabel } from "@/utils/levelLabels";
import type { Question } from "@shared/schema";

interface SortableQuestionItemProps {
  question: Question;
  index: number;
  topicName: string;
}

// Compact, draggable question card used in the Finalize "Test Order" list and
// the Edit Test "Order & Directions" tab: full stem + answer choices (2-col),
// topic + cognitive-level badges, correct answer marked with a left green
// check. Shared so the two screens never drift.
// "Question N" label hidden for now — flip to true to show item numbers again.
const SHOW_ITEM_NUMBER = false;

export function SortableQuestionItem({ question, index, topicName }: SortableQuestionItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: question.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Display-only sentence casing so the answer choices read the same as
  // everywhere else. The writer's own option order is never re-sorted —
  // stored order is displayed as-is. This does NOT affect the draggable
  // question order, which is governed by the page's ordered list.
  const display = normalizeAnswerCapitalization(question);

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="hover-elevate cursor-move" data-testid={`question-card-${index}`}>
        <CardContent className="py-2.5 px-4">
          <div className="flex items-start gap-3">
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mt-0.5">
              <GripVertical className="w-5 h-5 text-muted-foreground" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                {SHOW_ITEM_NUMBER && (
                  <span className="text-sm font-semibold text-foreground whitespace-nowrap">
                    Question {index + 1}
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground whitespace-nowrap">
                    {topicName}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                      question.level === "Knowledge"
                        ? "bg-blue-100 text-blue-700"
                        : question.level === "Understanding"
                        ? "bg-orange-100 text-orange-600"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {levelLabel(question.level)}
                  </span>
                </div>
              </div>

              <p className="text-sm text-foreground mb-2">{display.stem}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {display.answers.map((answer, i) => {
                  const isCorrect = i === display.correctAnswer;
                  return (
                    <div key={i} className="flex items-start gap-1.5">
                      {isCorrect ? (
                        <Check strokeWidth={3} className="mt-0.5 w-3 h-3 text-success shrink-0" />
                      ) : (
                        <span className="w-3 h-3 shrink-0" />
                      )}
                      <span className="text-muted-foreground">
                        <span className="font-medium mr-1">{answerLabel(i)})</span>
                        {answer}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
